import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import { MonthPicker } from "@/components/ui/monthpicker";
import { useForm, Controller } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils";
import { useDispatch, useSelector } from "react-redux";
import { getDestinationOptions } from "@/src/actions/tour/tourAction";

const formSchema = z.object({
  destination: z.string().min(1, "Destination is required"),
  month: z.string(),
});


function TourSearchForm({
  onSearch,
  sticky = false,
  className,
  initialValues,
}) {
  const dispatch = useDispatch();
  const { destinationOptions } = useSelector((state) => state.tours);
  const [openMonth, setOpenMonth] = useState(false);

  const methods = useForm({
    defaultValues: {
      destination: initialValues?.destination ?? "",
      month: initialValues?.month ?? "",
    },
    resolver: zodResolver(formSchema),
  });

  const { reset } = methods;

  // Scroll to top when used as sticky on results page
  useEffect(() => {
    if (sticky) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [sticky]);

  // Keep form in sync when URL/initialValues change
  useEffect(() => {
    if (initialValues) {
      reset({
        destination: initialValues.destination ?? "",
        month: initialValues.month ?? "",
        pageNo: initialValues.pageNo ?? "",
        pageSize: initialValues.pageSize ?? "",
      });
    }
  }, [initialValues?.destination, initialValues?.month, reset, initialValues?.pageNo, initialValues?.pageSize]);

  const handleSubmit = (data) => {
    if (onSearch) {
      const payload = {
        ...data,
        month: data.month || 0,
      };
      onSearch(payload);
    }
    console.log("Tour search payload:", data);
  };

  useEffect(() => {
    dispatch(getDestinationOptions())
  }, [])
  return (
    <div
      className={cn(
        sticky &&
        "md:sticky md:top-[72px] md:z-30 md:bg-background/95 md:backdrop-blur-sm",
        className
      )}
    >
      <Form {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmit)}
          className="mt-6 rounded-md border border-border bg-card/95 backdrop-blur-sm p-4 md:p-5 shadow-sm space-y-3"
        >
          <div className="flex flex-col gap-1">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Plan your trip
            </p>
            <h2 className="text-sm md:text-base font-semibold text-foreground">
              Find tour packages
            </h2>
          </div>

          <div className="grid gap-3 md:grid-cols-[2fr,1.5fr,auto]">
            {/* Destination select */}
            <div className="flex flex-col gap-1">
              <Controller
                name="destination"
                control={methods.control}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-muted-foreground">
                      Destination
                    </label>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-10 border-border bg-background text-sm">
                        <SelectValue placeholder="Select destination" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border border-border">
                        <SelectItem key={"0"} value={"0"} className="text-sm">
                          All Destinations
                        </SelectItem>
                        {destinationOptions?.map((d) => (
                          <SelectItem key={d.Value} value={d.Value} disabled={d.Disabled} className="text-sm">
                            {d.Text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {error && (
                      <p className="text-[11px] text-destructive mt-0.5">
                        {error.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Month picker */}
            <Controller
              name="month"
              control={methods.control}
              render={({ field, fieldState: { error } }) => (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    Month of travel
                  </label>

                  <Popover open={openMonth} onOpenChange={setOpenMonth}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        type="button"
                        className="h-10 w-full justify-between px-3 text-sm font-normal border-border bg-background text-foreground"
                      >
                        {field.value && field.value !== "0"
                          ? format(new Date(field.value + "-01"), "MMMM yyyy")
                          : "Select month"}
                        <ChevronDown className="h-4 w-4 opacity-60" />
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent
                      className="p-0 w-auto bg-popover border border-border"
                      align="start"
                    >
                      <MonthPicker
                        selectedMonth={
                          field.value && field.value !== "0" ? new Date(field.value + "-01") : undefined
                        }
                        onMonthSelect={(date) => {
                          if (date) {
                            const formatted = format(date, "yyyy-MM");
                            field.onChange(formatted);
                          }
                          setOpenMonth(false);
                        }}
                        minDate={new Date()}
                        maxDate={new Date(new Date().getFullYear() + 1, 11, 1)}
                      />
                    </PopoverContent>
                  </Popover>

                  {error && (
                    <p className="text-[11px] text-destructive mt-0.5">
                      {error.message}
                    </p>
                  )}
                </div>
              )}
            />

            {/* Search button */}
            <div className="flex items-end">
              <Button
                type="submit"
                className="w-full md:w-auto h-10 px-4 text-sm font-medium"
              >
                Search tours
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default TourSearchForm;
